import axios from "axios"

import type { AccessPolicy, AccessPolicyUpdate, RawEntityResponse } from "./_shared"
import type { HostedAgentHealth } from "./hosted-agents-health"
import type { HostedAgentLifecycleSummary } from "./hosted-agents-lifecycle"
import type { HostedAgentLink } from "./hosted-agents-links"

import { api } from "@/lib/api-client"

import { getRawEntity, saveRawEntity } from "./_shared"

// =============================================================================
// Hosted Agents admin client (HERMES Fas 5)
//
// Backend: routes/admin/hosted_agents.py — platform-only endpoints under
// /api/admin/hosted-agents. The <agentId> path segment accepts the canonical
// id ("agent:<uuid>") OR the agent's slug (the slug "templates" is reserved).
//
// Concurrency: ordinary PUT carries `revision` (409 "revision_conflict" when
// stale); the Raw path uses `expected_hash` like api/admin/_shared.ts (409
// "hash_mismatch"); create/instantiate 409 "slug_conflict". All three are
// rethrown as Error with `cause: { code, ... }`. Links (B4/B5) and lifecycle
// requests (B10/B13) live in hosted-agents-links.ts / hosted-agents-lifecycle.ts.
// =============================================================================

export type HostedAgentConflictCode = "slug_conflict" | "revision_conflict" | "hash_mismatch"

export type HostedAgentConflictCause = {
  code: HostedAgentConflictCode
  currentHash?: string
}

export type HostedAgentAvatar = {
  color: string
  initials: string
  character?: "nova" | "atlas" | "milo" | "alma" | "cleo" | "vega" | "otis" | "iris" | null
}

export type HostedAgentProfile = {
  name: string
  title: string
  description: string
  introduction?: string
  starter_prompts?: string[]
  avatar: HostedAgentAvatar
}

export type HostedAgentModelSelection = "auto" | "fixed"

export type HostedAgentReasoningEffort =
  | "none"
  | "minimal"
  | "low"
  | "medium"
  | "high"
  | "xhigh"
  | "max"

export type HostedAgentModelConfig = {
  selection: HostedAgentModelSelection
  model_id: string | null
  reasoning_effort: HostedAgentReasoningEffort
}

export type HostedAgentBudget = {
  daily_tokens: number | null
  monthly_tokens: number | null
  monthly_sek: number | null
  on_exceeded: "block" | "budget_model"
  /** #3039: warning-alert line in percent of each cap (1–99; server default 80). */
  alert_threshold_percent?: number
}

export type HostedAgentMemoryMode = "personal" | "open"

export type HostedAgentChannels = {
  portal: boolean
}

export type HostedAgentRuntimeLimits = {
  mem_limit: string
  pids_limit: number
  /** LLM↔tool rounds per run (server default 40). Absent on documents stored before it existed. */
  max_iterations?: number
  /** Web lookups per run (#3334, server default 30); past it they are refused. */
  max_web_tool_calls?: number
  /** Wall-clock seconds per run (#3334, server default 600); past it the run ends. */
  max_run_seconds?: number
}

/** Read-only secret summary from to_public_dict() — never the secret itself. */
export type HostedAgentSecretSummary = {
  configured: boolean
  version: number | null
  rotated_at: string | null
}

/** Last X-Hermes-Version the agent's container presented (Fas 2b). */
export type HostedAgentHermesVersion = {
  last_seen: string | null
  seen_at: string | null
}

/**
 * The public agent document (`to_public_dict()`): the stored definition
 * minus `secret_ref`, plus the read-only `secret` summary.
 */
export interface HostedAgent {
  /** Host admission receipt: read-only, never carried into a PUT. */
  managed_runtime?: Readonly<Record<string, unknown>> | null
  id: string
  tenant_id: string
  revision: number
  slug: string
  enabled: boolean
  profile: HostedAgentProfile
  instructions: string
  model: HostedAgentModelConfig
  tools: string[]
  tool_overrides: Record<string, unknown>
  /** A3.1 per-action tool allowlist: tool name → permitted action names (empty/absent = all). */
  tool_actions: Record<string, string[]>
  /**
   * Knowledge-library GRANT (P2-2): library ids `library_search` may query.
   * The gateway seeds retrieval from this binding when the client envelope
   * carries no selection (iOS); an envelope can only narrow it. Empty = none.
   */
  libraries: string[]
  library_contexts?: Record<string, string>
  skills: string[]
  /**
   * Installed skill packages (#3033): SERVER-MANAGED pins — never part of the
   * buffered PUT payload; mutate only via the install/uninstall/upgrade
   * endpoints (api/admin/skill-packages.ts). Optional: older backends never
   * send it.
   */
  skill_packages?: Array<{
    package_id: string
    name: string
    version: number
    content_sha256: string
    installed_at: string
    installed_by: string
  }>
  plugins: string[]
  memory_mode: HostedAgentMemoryMode
  budget: HostedAgentBudget
  access: AccessPolicy
  channels: HostedAgentChannels
  routines: unknown[]
  /** Outgoing AgentLink edges (B4/B5), managed via hosted-agents-links.ts; carried forward on PUT. */
  links: HostedAgentLink[]
  runtime: HostedAgentRuntimeLimits
  min_hermes_version: string | null
  template_id: string | null
  template_version: number | null
  secret: HostedAgentSecretSummary
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
  docType?: string
}

/**
 * A create/PUT payload: the definition minus server-managed fields
 * (id, tenant_id, revision, secret_ref/secret, created/updated stamps,
 * docType). Everything is optional on create — the service applies model
 * defaults — but `slug` and `profile.name` are required by validation.
 */
export type HostedAgentInput = {
  slug: string
  enabled?: boolean
  profile: Partial<HostedAgentProfile> & { name: string }
  instructions?: string
  model?: HostedAgentModelConfig
  tools?: string[]
  tool_overrides?: Record<string, unknown>
  tool_actions?: Record<string, string[]>
  libraries?: string[]
  library_contexts?: Record<string, string>
  skills?: string[]
  plugins?: string[]
  memory_mode?: HostedAgentMemoryMode
  budget?: HostedAgentBudget
  access?: AccessPolicy
  channels?: HostedAgentChannels
  runtime?: HostedAgentRuntimeLimits
  min_hermes_version?: string | null
  template_id?: string | null
  template_version?: number | null
}

/** Fields stripped/re-stamped by the server on every write path. */
export const HOSTED_AGENT_SERVER_MANAGED_FIELDS = [
  "id",
  "tenant_id",
  "revision",
  "secret_ref",
  "secret",
  "created_by",
  "created_at",
  "updated_by",
  "updated_at",
  "docType",
  // #3033 Fas 3: installed skill packages mutate only through the
  // install/uninstall/upgrade endpoints — the backend rejects a PUT that
  // carries them (services/hosted_agents/service.py::SERVER_MANAGED_FIELDS).
  "skill_packages",
  "managed_runtime",
] as const

/**
 * Project a public agent document down to a PUT payload by dropping the
 * server-managed fields (and the read-only `secret` summary). Use this to
 * build "carry everything forward, change one field" updates.
 */
export function toHostedAgentUpdatePayload(agent: HostedAgent): Record<string, unknown> {
  const managed = new Set<string>(HOSTED_AGENT_SERVER_MANAGED_FIELDS)
  return Object.fromEntries(
    Object.entries(agent).filter(([key]) => !managed.has(key) && !key.startsWith("_"))
  )
}

/** One budget window's live status (Fas 6 quota service). */
export type HostedAgentBudgetWindow = {
  window: "daily" | "monthly"
  unit: "tokens" | "sek"
  limit: number
  used: number
  reserved: number
  remaining: number
  percent: number
  exceeded: boolean
  resets_at: string
}

/**
 * Current-window consumption vs. the definition's budget caps (Fas 6).
 * `null` on a list/detail read means the quota service is not wired or the
 * rollup read failed — "cannot say", never "zero".
 */
export type HostedAgentBudgetStatus = {
  on_exceeded: "block" | "budget_model"
  exceeded: boolean
  windows: {
    daily_tokens: HostedAgentBudgetWindow | null
    monthly_tokens: HostedAgentBudgetWindow | null
    monthly_sek: HostedAgentBudgetWindow | null
  }
  usage: Record<string, unknown>
  as_of: string
}

/** List item: the public document + runtime observability (never stored). */
export interface HostedAgentListItem extends HostedAgent {
  paused: boolean
  paused_at: string | null
  hermes_version: HostedAgentHermesVersion
  budget_status: HostedAgentBudgetStatus | null
  /**
   * #3039 computed health; `null`/absent = health service not wired or the
   * read failed ("cannot say"), never "healthy". Absent on older backends.
   */
  health?: HostedAgentHealth | null
}

export interface HostedAgentDetail {
  agent: HostedAgent
  paused: boolean
  paused_at: string | null
  /** Claimed, still-executing runs; null = run service not wired ("cannot say"). */
  activeRuns: number | null
  hermes_version: HostedAgentHermesVersion
  budget_status: HostedAgentBudgetStatus | null
  /** Active (non-terminal) lifecycle request, if any (B10/B13); absent on older backends. */
  lifecycle?: HostedAgentLifecycleSummary | null
}

export type HostedAgentTemplateStatus = "available" | "coming_soon"

export interface HostedAgentTemplate {
  template_id: string
  version: number
  status: HostedAgentTemplateStatus
  name: string
  title: string
  description: string
  tools: string[]
  memory_mode: HostedAgentMemoryMode
  default_slug: string
  enabled_by_default: boolean
  /** Full definition payload the template seeds (create-shape, no server fields). */
  definition: Record<string, unknown>
}

export interface HostedAgentPauseState {
  success: boolean
  agent_id: string
  paused: boolean
  paused_at: string | null
}

export interface HostedAgentAccessUpdateResponse {
  success: boolean
  agent_id: string
  access: AccessPolicy
  revision: number
  message: string
}

export interface HostedAgentSelftestResult {
  passed: boolean
  run_id: string | null
  duration_ms: number | null
  detail: string
}

export interface RotateHostedAgentSecretResponse {
  success: boolean
  agent_id: string
  /** The plaintext secret — returned EXACTLY ONCE, never retrievable again. */
  secret: string
  secret_version: number
  rotated_at: string
  revision: number
  message: string
}

/**
 * Rethrow 409 conflicts (slug_conflict / revision_conflict / hash_mismatch)
 * as an Error whose message starts with the code and whose `cause` carries
 * `{ code, currentHash? }` — the same convention as saveRawEntity.
 */
function rethrowHostedAgentConflict(error: unknown): never {
  if (axios.isAxiosError(error) && error.response?.status === 409) {
    const data = error.response.data as
      | { code?: string; error?: string; current_hash?: string }
      | undefined
    const code = data?.code
    if (code === "slug_conflict" || code === "revision_conflict" || code === "hash_mismatch") {
      const cause: HostedAgentConflictCause = { code }
      if (typeof data?.current_hash === "string") cause.currentHash = data.current_hash
      throw new Error(`${code}: ${data?.error ?? "conflicting change on the hosted agent"}`, {
        cause,
      })
    }
  }
  throw error
}

/** Narrow an unknown error to a hosted-agent conflict cause, if it is one. */
export function getHostedAgentConflict(error: unknown): HostedAgentConflictCause | null {
  if (error instanceof Error && typeof error.cause === "object" && error.cause !== null) {
    const cause = error.cause as { code?: unknown; currentHash?: unknown }
    if (
      cause.code === "slug_conflict" ||
      cause.code === "revision_conflict" ||
      cause.code === "hash_mismatch"
    ) {
      return {
        code: cause.code,
        currentHash: typeof cause.currentHash === "string" ? cause.currentHash : undefined,
      }
    }
  }
  return null
}

// =============================================================================
// Collection
// =============================================================================

export async function getHostedAgents(): Promise<{
  agents: HostedAgentListItem[]
  count: number
}> {
  const response = await api.get("/admin/hosted-agents")
  const data = response.data || response
  return { agents: Array.isArray(data.agents) ? data.agents : [], count: data.count ?? 0 }
}

export async function createHostedAgent(payload: HostedAgentInput): Promise<HostedAgent> {
  try {
    const response = await api.post("/admin/hosted-agents", payload)
    const data = response.data || response
    if (data.success === false) {
      throw new Error(data.message || data.error || "Failed to create hosted agent")
    }
    return data.agent
  } catch (error) {
    rethrowHostedAgentConflict(error)
  }
}

// =============================================================================
// Templates (Fas 3 catalog)
// =============================================================================

export async function getHostedAgentTemplates(): Promise<HostedAgentTemplate[]> {
  const response = await api.get("/admin/hosted-agents/templates")
  const data = response.data || response
  return Array.isArray(data.templates) ? data.templates : []
}

/**
 * Instantiate a platform template into a tenant-owned agent. `overrides`
 * are deep-merged onto the template definition (overrides win; lists
 * replace) — pass e.g. `{ slug: "anbud-2" }` to avoid a slug conflict.
 */
export async function instantiateHostedAgentTemplate(
  templateId: string,
  overrides?: Record<string, unknown>
): Promise<HostedAgent> {
  try {
    const response = await api.post(
      `/admin/hosted-agents/templates/${encodeURIComponent(templateId)}/instantiate`,
      overrides && Object.keys(overrides).length > 0 ? { overrides } : {}
    )
    const data = response.data || response
    if (data.success === false) {
      throw new Error(data.message || data.error || "Failed to instantiate template")
    }
    return data.agent
  } catch (error) {
    rethrowHostedAgentConflict(error)
  }
}

// =============================================================================
// Item CRUD
// =============================================================================

export async function getHostedAgent(agentId: string): Promise<HostedAgentDetail> {
  const response = await api.get(`/admin/hosted-agents/${encodeURIComponent(agentId)}`)
  return response.data || response
}

/**
 * Replace the definition (PUT semantics). `revision` must be the stored
 * revision from the last read; a stale value 409s with "revision_conflict".
 */
export async function updateHostedAgent(
  agentId: string,
  agent: Record<string, unknown>,
  revision: number
): Promise<HostedAgent> {
  try {
    const response = await api.put(`/admin/hosted-agents/${encodeURIComponent(agentId)}`, {
      agent,
      revision,
    })
    const data = response.data || response
    if (data.success === false) {
      throw new Error(data.message || data.error || "Failed to update hosted agent")
    }
    return data.agent
  } catch (error) {
    rethrowHostedAgentConflict(error)
  }
}

/** Delete the agent; returns the deleted document. */
export async function deleteHostedAgent(agentId: string): Promise<HostedAgent> {
  const response = await api.delete(`/admin/hosted-agents/${encodeURIComponent(agentId)}`)
  const data = response.data || response
  if (data.success === false) {
    throw new Error(data.message || data.error || "Failed to delete hosted agent")
  }
  return data.agent
}

// =============================================================================
// Access policy
// =============================================================================

export async function updateHostedAgentAccessPolicy(
  agentId: string,
  policy: AccessPolicyUpdate
): Promise<HostedAgentAccessUpdateResponse> {
  try {
    const response = await api.put(
      `/admin/hosted-agents/${encodeURIComponent(agentId)}/access`,
      policy
    )
    const data = response.data || response
    if (data.success === false) {
      throw new Error(data.message || data.error || "Failed to update access policy")
    }
    return data
  } catch (error) {
    rethrowHostedAgentConflict(error)
  }
}

// =============================================================================
// Raw JSON (hash-guarded)
// =============================================================================

export async function getHostedAgentRaw(agentId: string): Promise<RawEntityResponse<HostedAgent>> {
  return getRawEntity<HostedAgent>(`/admin/hosted-agents/${encodeURIComponent(agentId)}/raw`)
}

/**
 * Raw save: "this JSON IS the entity". `expectedHash` comes from the latest
 * getHostedAgentRaw; `reason` must be at least 10 characters. Stale hash →
 * Error("hash_mismatch: …", { cause: { code, currentHash } }) via _shared.
 * Server-managed fields and the `secret` summary are frozen server-side.
 */
export async function saveHostedAgentRaw(args: {
  agentId: string
  entity: Record<string, unknown>
  expectedHash: string
  reason: string
  source?: string
}): Promise<{ success: boolean; entity: HostedAgent; hash: string }> {
  const result = await saveRawEntity<HostedAgent>({
    endpoint: `/admin/hosted-agents/${encodeURIComponent(args.agentId)}/raw`,
    entity: args.entity,
    expectedHash: args.expectedHash,
    reason: args.reason,
    source: args.source,
  })
  // The backend returns the saved document as `agent`; normalize to `entity`
  // so callers can consume the generic raw-save shape.
  const data = result as unknown as { success: boolean; agent?: HostedAgent; hash: string }
  return {
    success: data.success,
    entity: (data.agent ?? result.entity) as HostedAgent,
    hash: data.hash,
  }
}

// =============================================================================
// Pause / resume / selftest (Fas 2b ops)
// =============================================================================

export async function pauseHostedAgent(agentId: string): Promise<HostedAgentPauseState> {
  const response = await api.post(`/admin/hosted-agents/${encodeURIComponent(agentId)}/pause`)
  const data = response.data || response
  if (data.success === false) {
    throw new Error(data.message || data.error || "Failed to pause hosted agent")
  }
  return data
}

export async function resumeHostedAgent(agentId: string): Promise<HostedAgentPauseState> {
  const response = await api.post(`/admin/hosted-agents/${encodeURIComponent(agentId)}/resume`)
  const data = response.data || response
  if (data.success === false) {
    throw new Error(data.message || data.error || "Failed to resume hosted agent")
  }
  return data
}

/**
 * Run the golden-turn selftest through the real execution path and await
 * the verdict. `timeoutSeconds` is clamped server-side to 1..300
 * (default 150, server-configurable). The run is flagged
 * `initiator: selftest` and never counts as user traffic.
 */
export async function selftestHostedAgent(
  agentId: string,
  timeoutSeconds?: number
): Promise<HostedAgentSelftestResult> {
  const response = await api.post(
    `/admin/hosted-agents/${encodeURIComponent(agentId)}/selftest`,
    timeoutSeconds !== undefined ? { timeout_seconds: timeoutSeconds } : {}
  )
  return response.data || response
}

// =============================================================================
// Secret rotation (B9)
// =============================================================================

/**
 * Rotate the per-agent secret. The plaintext in the response is shown
 * EXACTLY ONCE and immediately invalidates the previous secret — surface it
 * in a copyable dialog and never persist it client-side.
 */
export async function rotateHostedAgentSecret(
  agentId: string
): Promise<RotateHostedAgentSecretResponse> {
  try {
    const response = await api.post(
      `/admin/hosted-agents/${encodeURIComponent(agentId)}/secret/rotate`
    )
    const data = response.data || response
    if (data.success === false) {
      throw new Error(data.message || data.error || "Failed to rotate hosted agent secret")
    }
    return data
  } catch (error) {
    rethrowHostedAgentConflict(error)
  }
}
